import { Environment } from 'dashboard/models/environment';
import { InquiryTemplate, InquiryTemplateFilterOption } from 'dashboard/models/inquiry-template';
import { Organization } from 'dashboard/models/organization';
import { OrganizationPlan } from 'dashboard/models/organization-plan';
import { OrganizationPlanMigration } from 'dashboard/models/organization-plan-migration';
import { ReportTemplate } from 'dashboard/models/report-template';
import { Template, TemplateFilterOption } from 'dashboard/models/template';
import { Theme } from 'dashboard/models/theme';
import {
  VerificationTemplate,
  VerificationTemplateFilterOption,
} from 'dashboard/models/verification-template';
import { Webhook } from 'dashboard/models/webhook';
import { OrganizationActions, OrganizationActionType } from 'dashboard/redux/actions/organization';

export interface OrganizationState {
  inquiryTemplates: InquiryTemplate[];
  inquiryTemplateFilterOptions: InquiryTemplateFilterOption[];
  verificationTemplates: VerificationTemplate[];
  verificationTemplateFilterOptions: VerificationTemplateFilterOption[];
  templates: Template[];
  templateFilterOptions: TemplateFilterOption[];
  environments: Environment[];
  organization: Organization;
  themes: Theme[];
  webhooks: Webhook[];
  reportTemplates: ReportTemplate[];
  currentOrganizationPlan: OrganizationPlan;
  pendingOrganizationPlanMigration: OrganizationPlanMigration;
}

export const initialOrganizationState: OrganizationState = {
  inquiryTemplates: [],
  inquiryTemplateFilterOptions: [],
  verificationTemplates: [],
  verificationTemplateFilterOptions: [],
  templates: [],
  templateFilterOptions: [],
  environments: [],
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  organization: undefined,
  themes: [],
  webhooks: [],
  reportTemplates: [],
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  currentOrganizationPlan: undefined,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  pendingOrganizationPlanMigration: undefined,
};

export default function (
  state: OrganizationState = initialOrganizationState,
  action: OrganizationActions,
): OrganizationState {
  switch (action.type) {
    case OrganizationActionType.SetInquiryTemplatesForOrganization: {
      return {
        ...state,
        inquiryTemplates: action.payload.inquiryTemplates,
      };
    }

    case OrganizationActionType.SetInquiryTemplateFilterOptions: {
      return {
        ...state,
        inquiryTemplateFilterOptions: action.payload.inquiryTemplateFilterOptions,
      };
    }

    case OrganizationActionType.SetVerificationTemplates: {
      return {
        ...state,
        verificationTemplates: action.payload.verificationTemplates,
      };
    }

    case OrganizationActionType.SetVerificationTemplateFilterOptions: {
      return {
        ...state,
        verificationTemplateFilterOptions: action.payload.verificationTemplateFilterOptions,
      };
    }

    case OrganizationActionType.SetTemplates: {
      return {
        ...state,
        templates: action.payload.templates,
      };
    }

    case OrganizationActionType.SetTemplateFilterOptions: {
      return {
        ...state,
        templateFilterOptions: action.payload.templateFilterOptions,
      };
    }

    case OrganizationActionType.SetTemplate: {
      const template = action.payload.template;
      const newTemplates = [...state.templates];

      const index = state.templates.map((b) => b.id).indexOf(template.id);
      if (index >= 0) {
        newTemplates[index] = template;
      } else {
        newTemplates.push(template);
      }

      return {
        ...state,
        templates: newTemplates,
      };
    }

    case OrganizationActionType.SetEnvironments: {
      return {
        ...state,
        environments: action.payload.environments,
      };
    }

    case OrganizationActionType.SetEnvironment: {
      // We can't use lodash's `union` because we have to set the environment in place
      // so that the tables on the Development page don't get moved around.
      const environment = action.payload.environment;
      const newEnvironments = [...state.environments];

      const index = state.environments.map((b) => b.id).indexOf(environment.id);
      if (index >= 0) {
        newEnvironments[index] = environment;
      } else {
        newEnvironments.push(environment);
      }

      return {
        ...state,
        environments: newEnvironments,
      };
    }

    case OrganizationActionType.SetOrganization: {
      return {
        ...state,
        organization: action.payload.organization,
      };
    }

    case OrganizationActionType.SetCurrentOrganizationPlan: {
      return {
        ...state,
        currentOrganizationPlan: action.payload.currentOrganizationPlan,
      };
    }

    case OrganizationActionType.SetPendingOrganizationPlanMigration: {
      return {
        ...state,
        pendingOrganizationPlanMigration: action.payload.pendingOrganizationPlanMigration,
      };
    }

    case OrganizationActionType.SetThemes: {
      return {
        ...state,
        themes: action.payload.themes,
      };
    }

    case OrganizationActionType.SetTheme: {
      const theme = action.payload.theme;
      const newThemes = [...state.themes];

      const index = state.themes.map((b) => b.id).indexOf(theme.id);
      if (index >= 0) {
        newThemes[index] = theme;
      } else {
        newThemes.push(theme);
      }

      return {
        ...state,
        themes: newThemes,
      };
    }

    default:
      return state;
  }
}
